import axios from 'axios';
import { isTokenExpiringSoon, isTokenExpired } from '../utils/tokenUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Configuración de axios
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Variable para controlar si hay una renovación en proceso
let isRefreshing = false;
let refreshSubscribers = [];

// Función para notificar a los suscriptores cuando se renueva el token
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

// Función para notificar a todos los suscriptores
const onTokenRefreshed = (newToken) => {
  refreshSubscribers.forEach(callback => callback(newToken));
  refreshSubscribers = [];
};

// Función para renovar el token
const refreshAccessToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await axios.post(`${API_BASE_URL}/v1/auth/refresh`, {
      refresh_token: refreshToken
    });

    const { access_token, refresh_token } = response.data;

    // Guardar nuevos tokens
    localStorage.setItem('token', access_token);
    localStorage.setItem('refreshToken', refresh_token);

    return access_token;
  } catch (error) {
    console.error('Error refreshing token:', error);
    // Si falla la renovación, limpiar todo y redirigir
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    window.location.href = '/login';
    throw error;
  }
};

// Interceptor para incluir el token en las peticiones
apiClient.interceptors.request.use(
  async (config) => {
    // No verificar token en requests de login o refresh
    if (config.url && (config.url.includes('/auth/login') || config.url.includes('/auth/refresh'))) {
      return config;
    }

    const token = localStorage.getItem('token');

    if (token) {
      // Verificar si el token está por expirar (dentro de 5 minutos)
      if (isTokenExpiringSoon(token, 5)) {
        console.log('Token está por expirar, renovando... (apiClient)');

        if (!isRefreshing) {
          isRefreshing = true;
          try {
            const newToken = await refreshAccessToken();
            isRefreshing = false;
            onTokenRefreshed(newToken);
            config.headers.Authorization = `Bearer ${newToken}`;
          } catch (error) {
            isRefreshing = false;
            return Promise.reject(error);
          }
        } else {
          // Si ya hay una renovación en proceso, esperar
          return new Promise((resolve) => {
            subscribeTokenRefresh((newToken) => {
              config.headers.Authorization = `Bearer ${newToken}`;
              resolve(config);
            });
          });
        }
      } else {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores de respuesta
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Si es un error 401 y no es un request de login/refresh
    if (error.response?.status === 401 &&
        !originalRequest._retry &&
        !originalRequest.url.includes('/auth/login') &&
        !originalRequest.url.includes('/auth/refresh')) {

      originalRequest._retry = true;

      // Intentar renovar el token
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await refreshAccessToken();
          isRefreshing = false;
          onTokenRefreshed(newToken);

          // Reintentar la petición original con el nuevo token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          return Promise.reject(refreshError);
        }
      } else {
        // Si ya hay una renovación en proceso, esperar
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(apiClient(originalRequest));
          });
        });
      }
    }

    return Promise.reject(error);
  }
);

// Funciones auxiliares para manejar errores
export const handleApiError = (error) => {
  console.error('API Error:', error);
  
  if (error.response) {
    // Error del servidor con respuesta
    const { status, data } = error.response;
    
    // Manejar errores de validación de FastAPI/Pydantic (formato 1)
    if (data.detail && Array.isArray(data.detail)) {
      const validationErrors = {};
      data.detail.forEach(err => {
        const field = err.loc && err.loc.length > 1 ? err.loc[1] : 'general';
        validationErrors[field] = err.msg || err.message;
      });

      return {
        success: false,
        message: 'Error de validación en los datos enviados',
        detail: data.detail, // Incluir detail original para traducción personalizada
        validation_errors: Object.entries(validationErrors).map(([field, message]) => ({
          field,
          message
        })),
        validationErrors,
        status
      };
    }
    
    // Manejar errores de validación personalizados (formato 2)
    if (data.validation_errors && Array.isArray(data.validation_errors)) {
      const validationErrors = {};
      data.validation_errors.forEach(err => {
        validationErrors[err.field] = err.message;
      });

      return {
        success: false,
        message: data.message || 'Error de validación en los datos enviados',
        validation_errors: data.validation_errors.map(err => ({
          field: err.field,
          message: err.message
        })),
        validationErrors,
        status
      };
    }

    // Manejar errores con detail como string (formato 3)
    if (data.detail && typeof data.detail === 'string') {
      return {
        success: false,
        message: data.detail,
        status
      };
    }

    // Error general
    return {
      success: false,
      message: data.message || data.detail || `Error ${status}: ${error.response.statusText}`,
      status
    };
  } else if (error.request) {
    // Error de red
    return {
      success: false,
      message: 'Error de conexión. Verifica tu conexión a internet e intenta nuevamente.',
      status: 0
    };
  } else {
    // Error de configuración
    return {
      success: false,
      message: error.message || 'Error inesperado',
      status: 0
    };
  }
};

// Función para formatear datos de respuesta
export const formatApiResponse = (response) => {
  // Varios endpoints devuelven la paginación en campos planos
  // ({ total, page, per_page }) en lugar de un objeto `pagination`.
  const buildPagination = (data) => {
    if (data.pagination) return data.pagination;
    if (data.total === undefined || !data.per_page) return null;
    return {
      total: data.total,
      page: data.page || 1,
      per_page: data.per_page,
      pages: Math.max(1, Math.ceil(data.total / data.per_page))
    };
  };

  const result = {
    success: response.data.success !== undefined ? response.data.success : true,
    data: response.data.data || response.data,
    message: response.data.message || 'Operación exitosa',
    pagination: buildPagination(response.data),
    meta: response.data.meta || null
  };
  
  // Manejar errores de validación si existen
  if (response.data.validation_errors && Array.isArray(response.data.validation_errors)) {
    const validationErrors = {};
    response.data.validation_errors.forEach(err => {
      validationErrors[err.field] = err.message;
    });
    
    result.validation_errors = response.data.validation_errors;
    result.validationErrors = validationErrors;
  }
  
  return result;
};

export default apiClient;
